import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/', internal: true },
    { label: 'Piano Removals London', href: '/piano-removals-london', internal: true },
    { label: 'Grand Piano Removal', href: '/grand-piano-removal-london', internal: true },
    { label: 'Upright Piano Removal', href: '/upright-piano-removal-london', internal: true },
    { label: 'Costs', href: '/piano-removal-cost-london', internal: true },
    { label: 'Contact', href: '/contact-us', internal: true },
  ];

  const handleNavClick = (href: string, internal?: boolean) => {
    setMobileOpen(false);
    if (!internal && href.startsWith('#')) {
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 ${
        isScrolled ? 'bg-[#0e0d0b]/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
      aria-label="Main navigation"
    >
      {/* Top utility bar */}
      <div
        className={`overflow-hidden ${
          isScrolled ? 'max-h-0 opacity-0' : 'max-h-10 opacity-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-xs text-[#b0b0a8]">
          <div className="flex items-center gap-5">
            <a
              href="tel:02034419463"
              className="flex items-center gap-1.5 hover:text-[#c9a84c] transition-colors cursor-pointer"
              aria-label="Call us"
            >
              <i className="ri-phone-line text-[#c9a84c]" aria-hidden="true"></i>
              020 3441 9463
            </a>
            <span className="hidden sm:flex items-center gap-1.5">
              <i className="ri-time-line text-[#c9a84c]" aria-hidden="true"></i>
              Mon–Fri 08:30–18:00 | Sat 08:30–15:00
            </span>
          </div>
          <a
            href="mailto:thenorthpiano@googlemail.com"
            className="hidden sm:flex items-center gap-1.5 hover:text-[#c9a84c] transition-colors cursor-pointer"
            aria-label="Email us"
          >
            <i className="ri-mail-line text-[#c9a84c]" aria-hidden="true"></i>
            thenorthpiano@googlemail.com
          </a>
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="border-b border-white/10"></div>
        </div>
      </div>

      {/* Main navbar */}
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0" aria-label="Piano Movers UK - Home">
          <div className="w-9 h-9 flex items-center justify-center">
            <i className="ri-music-2-line text-2xl text-[#c9a84c]" aria-hidden="true"></i>
          </div>
          <span className="text-[#f5f5f0] font-serif font-bold text-lg tracking-wide">
            PIANO MOVERS UK
          </span>
        </Link>

        {/* Desktop nav links */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            link.internal ? (
              <Link
                key={link.label}
                to={link.href}
                className="text-[#d4d4ce] text-sm font-medium hover:text-[#c9a84c] transition-colors cursor-pointer relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#c9a84c] after:transition-all hover:after:w-full whitespace-nowrap"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href, link.internal);
                }}
                className="text-[#d4d4ce] text-sm font-medium hover:text-[#c9a84c] transition-colors cursor-pointer relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#c9a84c] after:transition-all hover:after:w-full whitespace-nowrap"
              >
                {link.label}
              </a>
            )
          ))}
        </div>

        {/* Right side: phone + CTA + mobile toggle */}
        <div className="flex items-center gap-4">
          <a
            href="tel:02034419463"
            className="hidden md:flex items-center gap-2 text-[#c9a84c] text-sm font-semibold hover:text-[#d4b55f] transition-colors cursor-pointer"
            aria-label="Call Piano Movers UK"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full border border-[#c9a84c]/30">
              <i className="ri-phone-line text-sm" aria-hidden="true"></i>
            </div>
            <span className="hidden xl:inline whitespace-nowrap">020 3441 9463</span>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-9 h-9 flex items-center justify-center text-[#f5f5f0] cursor-pointer"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#0e0d0b]/95 backdrop-blur-sm border-t border-white/10 px-6 py-4 space-y-1">
          {navLinks.map((link) => (
            link.internal ? (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="block py-3 text-[#d4d4ce] text-sm font-medium hover:text-[#c9a84c] transition-colors cursor-pointer border-b border-white/5 last:border-0"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href, link.internal);
                }}
                className="block py-3 text-[#d4d4ce] text-sm font-medium hover:text-[#c9a84c] transition-colors cursor-pointer border-b border-white/5 last:border-0"
              >
                {link.label}
              </a>
            )
          ))}
          <div className="pt-3 flex items-center gap-4 text-xs text-[#b0b0a8]">
            <a href="tel:02034419463" className="flex items-center gap-1.5 hover:text-[#c9a84c] transition-colors cursor-pointer">
              <i className="ri-phone-line text-[#c9a84c]" aria-hidden="true"></i>
              020 3441 9463
            </a>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-[#c9a84c]" aria-hidden="true"></i>
              Mon–Sat
            </span>
          </div>
        </div>
      </div>
    </nav>
  );
}